import { ngFrameworkLoaded } from "@sazka/ng/ng-integration";
import { useNgSession } from "@sazka/ng/ng-session";
import { getPlayerAccount } from "./http-clients/player-account-http-client";
import { useLocalStorage, useSessionStorage } from "@sazka/utils/use-storage";
import { ref, watch } from "vue";
import { showAlert } from "@sazka/design-system/src/modal-alert/mount-alert";
import { getSettingValue } from "@sazka/utils/settings-api";
import { WecomaConstants } from "@sazka/utils/gdpr/wecoma-constants";

const sessionStorage = useSessionStorage();
const localStorage = useLocalStorage();
const { isLoggedIn } = useNgSession();
const storageName = "account-type";

export const isFullAccount = ref<boolean>(sessionStorage.getItem(storageName) === "full" ? true : null);
export const isTerminatedAccount = ref<boolean>(null);

const checkAccount = async (): Promise<boolean> => {
    if (isFullAccount.value) {
        return;
    }

    try {
        const playerAccount = await getPlayerAccount();
        isFullAccount.value = playerAccount.TempAccountStatus.LimitedAccountTypeStatus === "None";
        isTerminatedAccount.value =
            playerAccount.TempAccountStatus.AccountTerminationStatus === "Termination requested" &&
            playerAccount.TempAccountStatus.AccountTerminationSuspensionReason === "Personal reasons";
    } catch {
        return;
    }

    if (isFullAccount.value) {
        sessionStorage.setItem(storageName, "full", "essential");
    }
};

const isRegistrationPage = () => {
    return (
        environment.nodeAliasPath === "/registrace" ||
        window.location.pathname === "/mobilni-aplikace/vklad" ||
        window.location.pathname === "/registrace/mock-bank" ||
        window.location.pathname === "/odstavka-registrace"
    );
};

const showTerminatedAccountPopup = async () => {
    const text = await getSettingValue("TerminatedAccountMessage");

    showAlert({
        title: "Účet v procesu rušení",
        subText: text,
        btnCloseText: "Rozumím",
    });
};

export const clearAccountState = () => {
    isFullAccount.value = null;
    sessionStorage.removeItem(storageName);
};

export const usePlayerAccount = async () => {
    if (environment.isInAppView) {
        return;
    }

    await ngFrameworkLoaded();

    // if player with temporary account opens other page than registration, they are logged out
    if (!isRegistrationPage() && isLoggedIn.value) {
        await checkAccount();

        // check specifically for false, suppose it is full account in case of null
        if (isFullAccount.value === false) {
            ng_fw.logout();
        }
    }

    watch(isLoggedIn, async (loggedIn) => {
        if (loggedIn) {
            await checkAccount();

            // if player logs in with temporary account, they are redirected to pending registration
            // check specifically for false, suppose it is full account in case of null)
            if (!isRegistrationPage() && isFullAccount.value === false) {
                window.location.href = "/registrace";
                return;
            }

            if (isTerminatedAccount.value) {
                showTerminatedAccountPopup();
            }
        } else {
            clearAccountState();

            isTerminatedAccount.value = null;
        }
    });
};

// watch logout without waiting for NG framework because expired session is handled during NG framework loading
watch(isLoggedIn, async (loggedIn) => {
    if (!loggedIn) {
        localStorage.removeItem(WecomaConstants.WECOMA_KEY_STORAGE);
    }
});
